import {
  evaluateFitment,
  getFitmentEvaluation,
  normalizeBoltPattern,
  normalizeRimSpecs,
  normalizeTireCombos,
  parseVariantSpec,
  resolveDriveType,
  resolveOemOffsetRange,
  resolveWheelBackspacing,
} from '@theme/fitment-matcher';

const DISCLAIMER =
  'Estimates only — based on rim specs and OEM data, not your exact tire size or measured clearances. Confirm with a professional installer before purchasing lift kits, spacers, hub rings, or adapters.';

/** @typedef {import('@theme/fitment-matcher').FitmentLevel} FitmentLevel */

/**
 * @param {ReturnType<normalizeTireCombos>} tireCombos
 * @param {number} wheelDiameter
 */
function oemBackspacingLabel(tireCombos, wheelDiameter) {
  const withBackspacing = tireCombos.filter((combo) => combo.backspacingInches != null);
  if (!withBackspacing.length) return '—';

  const atDiameter = withBackspacing.filter((combo) => Number(combo.rimDiameter) === wheelDiameter);
  const source = atDiameter.length ? atDiameter : withBackspacing;
  const values = source.map((combo) => Number(combo.backspacingInches));
  const min = Math.min(...values);
  const max = Math.max(...values);

  return min === max ? `${min.toFixed(2)}"` : `${min.toFixed(2)}–${max.toFixed(2)}"`;
}

/**
 * @param {{ width: number | null, diameter: number }[]} rimSpecs
 */
function largestOemRim(rimSpecs) {
  const maxDiameter = Math.max(...rimSpecs.map((spec) => Number(spec.diameter)));
  const atMax = rimSpecs.filter((spec) => Number(spec.diameter) === maxDiameter);
  const widths = atMax.map((spec) => spec.width).filter((value) => value != null);
  return {
    diameter: maxDiameter,
    width: widths.length ? Math.max(...widths.map(Number)) : null,
  };
}

/**
 * @param {number | null} width
 */
function formatSize(diameter, width) {
  if (width != null) return `${diameter}×${width}"`;
  return `${diameter}"`;
}

/**
 * @param {{ diameter: number, width: number, bolt: string, et: number, bore: number | null, backspacingInches: number | null }} variantSpec
 * @param {Record<string, unknown>} vehicle
 * @param {ReturnType<typeof evaluateFitment>} evaluation
 */
export function buildFitmentAdvice(variantSpec, vehicle, evaluation) {
  /** @type {{ label: string, vehicle: string, wheel: string, ok: boolean | null, hint?: string }[]} */
  const comparison = [];

  const rimSpecs = normalizeRimSpecs(vehicle);
  const tireCombos = normalizeTireCombos(vehicle.tire_combos);
  const { etMin, etMax } = resolveOemOffsetRange(vehicle, variantSpec.diameter);
  const vehicleBolt = vehicle.bolt_pattern ? normalizeBoltPattern(String(vehicle.bolt_pattern)) : '';
  const vehicleBore = vehicle.center_bore != null ? Number(vehicle.center_bore) : null;
  const largestOem = rimSpecs.length ? largestOemRim(rimSpecs) : null;
  const driveType = evaluation.driveType ?? resolveDriveType(vehicle);

  const wheelBackspacing = resolveWheelBackspacing(variantSpec).inches;

  const isPlusDiameter =
    largestOem != null && variantSpec.diameter > largestOem.diameter;
  const isPlusWidth =
    largestOem?.width != null && variantSpec.width > Number(largestOem.width);

  comparison.push({
    label: 'Wheel size',
    vehicle: largestOem ? `Up to ${formatSize(largestOem.diameter, largestOem.width)}` : '—',
    wheel: formatSize(variantSpec.diameter, variantSpec.width),
    ok:
      largestOem != null
        ? isPlusDiameter || isPlusWidth
          ? null
          : true
        : null,
    hint:
      isPlusDiameter && (driveType === 'DRW' || evaluation.driveType === 'DRW')
        ? `${formatSize(variantSpec.diameter, variantSpec.width)} is a common aftermarket wide spec — lift kit and fender trimming may be required.`
        : isPlusDiameter
          ? `${variantSpec.diameter}" exceeds OEM max ${largestOem?.diameter}" — confirm tire and fender clearance.`
          : isPlusWidth
            ? `${variantSpec.width}" is wider than OEM — confirm inner and outer clearance.`
            : undefined,
  });

  const boltMatches = vehicleBolt ? variantSpec.bolt === vehicleBolt : null;
  comparison.push({
    label: 'Bolt pattern',
    vehicle: vehicleBolt ? vehicleBolt.toUpperCase() : '—',
    wheel: variantSpec.bolt.toUpperCase(),
    ok: vehicleBolt ? (boltMatches ? true : evaluation.needsWheelAdapter ? null : false) : null,
    hint: evaluation.needsWheelAdapter
      ? `Requires ${vehicleBolt.toUpperCase()} to ${variantSpec.bolt.toUpperCase()} wheel adapter`
      : undefined,
  });

  const etInRange =
    etMin != null &&
    etMax != null &&
    !Number.isNaN(etMin) &&
    !Number.isNaN(etMax) &&
    variantSpec.et >= etMin &&
    variantSpec.et <= etMax;
  const isDuallyStance =
    driveType === 'DRW' && etMin != null && !Number.isNaN(etMin) && variantSpec.et < etMin;

  comparison.push({
    label: 'Offset (ET)',
    vehicle:
      etMin != null && etMax != null && !Number.isNaN(etMin) && !Number.isNaN(etMax)
        ? `${etMin}–${etMax} mm`
        : '—',
    wheel: `${variantSpec.et} mm`,
    ok:
      etMin != null && etMax != null && !Number.isNaN(etMin) && !Number.isNaN(etMax)
        ? etInRange
          ? true
          : isDuallyStance || evaluation.pokeInches != null
            ? null
            : variantSpec.et > etMax
              ? null
              : false
        : null,
    hint:
      isDuallyStance && evaluation.pokeInches != null
        ? `Stance setup — wheel may poke ~${evaluation.pokeInches.toFixed(2)}" outward; fender flares may be needed.`
        : isDuallyStance
          ? 'Negative offset is common for dually-to-single-wheel conversions — confirm stance and clearance.'
          : evaluation.pokeInches != null
            ? `Wheel sits ~${evaluation.pokeInches.toFixed(2)}" further out than OEM.`
            : undefined,
  });

  comparison.push({
    label: 'Backspacing',
    vehicle: oemBackspacingLabel(tireCombos, variantSpec.diameter),
    wheel: `${wheelBackspacing.toFixed(2)}"`,
    ok:
      evaluation.layer2Pass === false
        ? false
        : evaluation.pokeInches != null
          ? null
          : evaluation.layer2Pass,
    hint:
      evaluation.layer2Pass === false
        ? 'Backspacing may interfere with brakes or suspension.'
        : evaluation.pokeInches != null
          ? `This wheel's backspacing is much smaller than OEM, so inner brake/suspension clearance is safe — but it shifts the whole wheel outward by ~${evaluation.pokeInches.toFixed(2)}". A smaller number here does NOT mean "close to stock"; see the Stick-out figure above. Wider off-road tires will add even more poke.`
          : undefined,
  });

  if (vehicleBore != null && !Number.isNaN(vehicleBore) && variantSpec.bore != null) {
    const adapterPath = evaluation.needsWheelAdapter;
    const boreFits = adapterPath ? true : variantSpec.bore >= vehicleBore - 0.5;
    comparison.push({
      label: 'Center bore',
      vehicle: `${vehicleBore} mm`,
      wheel: `${variantSpec.bore} mm`,
      ok: boreFits ? (evaluation.needsHubRing || adapterPath ? null : true) : false,
      hint: adapterPath
        ? 'With wheel adapters, bore must match the adapter pilot — not the OEM hub directly.'
        : evaluation.needsHubRing
          ? 'Hub-centric ring recommended.'
          : undefined,
    });
  }

  return {
    level: evaluation.level,
    headline: fitmentHeadline(evaluation),
    notes: evaluation.notes,
    comparison,
    disclaimer: DISCLAIMER,
  };
}

/**
 * @param {ReturnType<typeof evaluateFitment>} evaluation
 */
export function fitmentHeadline(evaluation) {
  if (evaluation.needsWheelAdapter) {
    return 'Requires wheel adapters — review notes below';
  }

  return fitmentLevelLabel(evaluation.level);
}

/**
 * @param {FitmentLevel} level
 */
export function fitmentLevelLabel(level) {
  switch (level) {
    case 'direct_fit':
      return 'Direct fit — no modifications expected';
    case 'modification_required':
      return 'Fits with modifications — review notes below';
    case 'no_fit':
      return 'Does not fit your selected vehicle';
    case 'unknown':
      return 'Fitment could not be fully verified';
    default:
      return '';
  }
}

/**
 * @param {HTMLElement} container
 * @param {{ level: FitmentLevel, headline?: string, notes: string[], comparison: { label: string, vehicle: string, wheel: string, ok: boolean | null, hint?: string }[], disclaimer: string } | null} advice
 */
export function renderFitmentAdvisor(container, advice) {
  const content = container.querySelector('[data-fitment-advisor-content]');
  if (!(content instanceof HTMLElement)) return;

  content.replaceChildren();

  if (!advice) {
    container.classList.add('hidden');
    container.dataset.fitmentLevel = '';
    return;
  }

  if (advice.level === 'direct_fit') {
    container.classList.add('hidden');
    container.dataset.fitmentLevel = advice.level;
    return;
  }

  container.classList.add('hidden');
  container.dataset.fitmentLevel = advice.level;
  container.classList.remove(
    'fitment-advisor--direct-fit',
    'fitment-advisor--modification',
    'fitment-advisor--no-fit',
    'fitment-advisor--unknown'
  );
  container.classList.add(`fitment-advisor--${advice.level.replace(/_/g, '-')}`);

  const intro = document.createElement('p');
  intro.className = 'fitment-advisor__intro';
  intro.textContent = advice.headline ?? fitmentLevelLabel(advice.level);
  content.appendChild(intro);

  if (advice.notes.length) {
    const notesList = document.createElement('ul');
    notesList.className = 'fitment-advisor__notes';
    advice.notes.forEach((note) => {
      const item = document.createElement('li');
      item.textContent = note;
      notesList.appendChild(item);
    });
    content.appendChild(notesList);
  }

  const table = document.createElement('table');
  table.className = 'fitment-advisor__table';
  table.innerHTML = `
    <thead>
      <tr>
        <th scope="col">Spec</th>
        <th scope="col">Your vehicle</th>
        <th scope="col">This wheel</th>
      </tr>
    </thead>
  `;

  const tbody = document.createElement('tbody');
  advice.comparison.forEach((row) => {
    const tr = document.createElement('tr');
    if (row.ok === true) tr.classList.add('fitment-advisor__row--ok');
    if (row.ok === false) tr.classList.add('fitment-advisor__row--issue');
    if (row.ok === null) tr.classList.add('fitment-advisor__row--caution');

    const status = row.ok === true ? ' ✓' : row.ok === false ? ' ✗' : row.ok === null ? ' ⚠' : '';
    const hintAttr = row.hint ? ` title="${row.hint.replace(/"/g, '&quot;')}"` : '';

    tr.innerHTML = `
      <th scope="row">${row.label}</th>
      <td>${row.vehicle}</td>
      <td${hintAttr}>${row.wheel}${status}</td>
    `;
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);
  content.appendChild(table);

  const disclaimer = document.createElement('p');
  disclaimer.className = 'fitment-advisor__disclaimer';
  disclaimer.textContent = advice.disclaimer;
  content.appendChild(disclaimer);
}

/**
 * @param {string} optionText
 * @param {Record<string, unknown> | null} vehicle
 * @param {FitmentLevel} result
 */
export function shouldShowFitmentAdvisor(optionText, vehicle, result) {
  if (result === 'direct_fit' || result === 'no_vehicle') return false;
  if (!vehicle) return false;
  return Boolean(parseVariantSpec(optionText));
}

/**
 * @param {string} optionText
 * @param {Record<string, unknown> | null} vehicle
 */
export function getFitmentAdvice(optionText, vehicle) {
  if (!vehicle) return null;
  const variantSpec = parseVariantSpec(optionText);
  if (!variantSpec) return null;
  const evaluation = evaluateFitment(variantSpec, vehicle);
  return buildFitmentAdvice(variantSpec, vehicle, evaluation);
}

export { parseVariantSpec, getFitmentEvaluation };
